import { Suspense, useEffect, useState } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { NgoCampaignCard } from './ngo-campaign-card';
import { NgoCampaignsGridSkeleton } from './ngo-campaign-card-skeleton';
import type { PartnerNgo } from './use-partner-ngos';

/**
 * The hand-off's grid is 2x2, so a page is four cards. The backend now answers at most three
 * campaigns (the September 2026 review's rule), so the arrows are in practice never shown — the
 * paging stays because the limit is the backend's to change, not this component's.
 */
const CAMPAIGNS_PER_PAGE = 4;

/** A 32px round control from the modal's top bar. White on the scrim, per the hand-off. */
function BarButton(props: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={props.onClick}
			disabled={props.disabled}
			aria-label={props.label}
			className="text-ink grid size-8 shrink-0 cursor-pointer place-items-center rounded-full bg-white transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-40"
		>
			{props.children}
		</button>
	);
}

/**
 * The NGO's most recent campaigns, one page at a time.
 *
 * Its own component so the Suspense boundary covers the grid alone — the sheet, its header and the
 * NGO lockup are already known when the modal opens and must not wait on a fetch. It owns the page
 * count and reports it upward, because only it knows how many campaigns came back.
 */
function CampaignsGrid(props: { ngo: PartnerNgo; page: number; onPageCount: (count: number) => void }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	// The cards link to another origin (the merchant's portal subdomain), which is derived from the
	// host serving this page rather than hardcoded — see `buildCampaignHref`.
	const { baseUrl } = useRouteContext({ from: '__root__' });
	const { data } = useSuspenseQuery(
		trpc.portal.getPartnerNgoCampaigns.queryOptions({ ngoId: props.ngo.id, locale: language }),
	);

	const pageCount = Math.max(1, Math.ceil(data.campaigns.length / CAMPAIGNS_PER_PAGE));
	const onPageCount = props.onPageCount;
	useEffect(() => onPageCount(pageCount), [pageCount, onPageCount]);

	if (data.campaigns.length === 0) {
		// lingui disallows a property access inside t`` — bind it to a plain variable first.
		const name = props.ngo.name;
		const noCampaignsCopy = t`${name} has not had any campaign recently. Take a look at the other organisations in the directory.`;
		return (
			<div className="flex flex-col items-center gap-4 py-16 text-center">
				<img
					src="/redesign/partner-ngos/illo-folder.svg"
					alt=""
					aria-hidden
					className="h-auto w-[200px]"
					width={200}
					height={159}
				/>
				<div className="flex flex-col gap-1">
					<p className="text-xl leading-[1.3] font-medium">{t`No campaigns to show right now.`}</p>
					<p className="text-ink/60 text-sm leading-[1.5]">{noCampaignsCopy}</p>
				</div>
			</div>
		);
	}

	const start = props.page * CAMPAIGNS_PER_PAGE;
	return (
		<div className="grid gap-6 xl:grid-cols-2">
			{data.campaigns.slice(start, start + CAMPAIGNS_PER_PAGE).map((campaign) => (
				<NgoCampaignCard key={campaign.id} campaign={campaign} baseUrl={baseUrl} />
			))}
		</div>
	);
}

/**
 * "Mira las campañas más recientes" — the sheet the directory opens when a partner's logo is clicked.
 *
 * The top-bar arrows page through THIS partner's campaigns, four at a time. They used to step between
 * NGOs, which read as navigating away from the thing the visitor had just clicked; the sheet is about
 * one organisation, and closing it is how you get back to the others.
 *
 * Built from the Radix primitives rather than the shared `DialogContent`, which is a centred, sm-width
 * app dialog: this one is a full-bleed bottom sheet with its own scrim (ink at 25% behind a 3px blur)
 * and a control bar that sits OUTSIDE the white panel. Overriding all of that through `className`
 * would be a longer and more fragile list than composing the four primitives directly.
 */
export function NgoCampaignsModal(props: { ngo: PartnerNgo | undefined }) {
	const { t } = useLingui();
	const navigate = useNavigate();
	const [page, setPage] = useState(0);
	const [pageCount, setPageCount] = useState(1);
	const ngo = props.ngo;
	const ngoId = ngo?.id;

	// Closing is a NAVIGATION, back to the directory's own URL — the sheet is open because the route
	// says so, so nothing else can close it. `replace` so a visitor who opened three partners in a row
	// does not have to walk back through all three to leave the page.
	const close = () => void navigate({ to: '/www/partner-ngos', replace: true, resetScroll: false });

	// Opening a different partner starts at its first page rather than wherever the last one was left.
	useEffect(() => {
		setPage(0);
		setPageCount(1);
	}, [ngoId]);

	return (
		<DialogPrimitive.Root open={!!ngo} onOpenChange={(open) => !open && close()}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 z-50 bg-[#25302c]/25 backdrop-blur-[3px]" />
				{/* Transparent to pointers, re-enabled on the bar controls and the panel below: the content box
				    spans the whole viewport under the 32px strip, so every click on the visible scrim landed on
				    it and Radix never saw a press *outside* the sheet — the close button was the only way out.
				    Ignored by pointers, those clicks reach the overlay (which pins itself to `auto`) instead.

				    It has to be `style`, not a class: a modal `DismissableLayer` writes `pointerEvents: "auto"`
				    inline on its own node while the body is pointer-events-disabled, which outranks any class.
				    Radix spreads `props.style` after its own value, so this is the supported way to override
				    it. A class appeared to work only until the layer re-rendered — the set it reads is still
				    empty on the first pass, so the inline value starts out absent and lands a tick later. */}
				<DialogPrimitive.Content
					className="data-open:animate-in data-closed:animate-out data-closed:slide-out-to-bottom data-open:slide-in-from-bottom fixed inset-x-0 top-8 bottom-0 z-50 flex flex-col gap-8 duration-200 outline-none"
					style={{ pointerEvents: 'none' }}
					aria-describedby={undefined}
				>
					{ngo && (
						/* Keyed on the partner so everything below is a fresh subtree whenever the sheet is
						   reopened for a different one. Without it the lockup kept the previous partner's
						   artwork: the `<picture>` element was reused, and a browser that has already picked a
						   candidate out of `srcSet` does not redo that choice when the attribute changes
						   underneath it. `contents` keeps the wrapper out of the flex layout. */
						<div key={ngo.id} className="contents">
							<div className="mx-auto flex w-full max-w-[1376px] shrink-0 items-center px-4">
								{/* Only once there is a second page to reach. A partner with four campaigns or fewer
								   showed two permanently-disabled arrows, which reads as something broken rather
								   than as an absence. `pageCount` starts at 1 and is corrected by the grid when the
								   query lands, so they appear with the campaigns rather than flickering before them. */}
								{pageCount > 1 && (
									<div className="pointer-events-auto flex items-center gap-2">
										<BarButton
											label={t`Previous campaigns`}
											onClick={() => setPage((current) => current - 1)}
											disabled={page <= 0}
										>
											<ArrowLeft className="size-4" />
										</BarButton>
										<BarButton
											label={t`More campaigns`}
											onClick={() => setPage((current) => current + 1)}
											disabled={page >= pageCount - 1}
										>
											<ArrowRight className="size-4" />
										</BarButton>
									</div>
								)}
								{/* `ml-auto` on a wrapper rather than `justify-between`: with the arrows gone the close
								   button is the only child and `justify-between` would park it on the left. It goes on
								   the wrapper because `asChild` hands its props to `BarButton`, which does not spread
								   them onto the element. */}
								<div className="pointer-events-auto ml-auto">
									<DialogPrimitive.Close asChild>
										<BarButton label={t`Close`} onClick={close}>
											<X className="size-4" />
										</BarButton>
									</DialogPrimitive.Close>
								</div>
							</div>

							{/* The panel runs off the bottom of the viewport by design — only its top corners are
							    rounded, and it is the scroll container for the whole grid. */}
							<div className="pointer-events-auto flex-1 overflow-y-auto rounded-t-3xl bg-white">
								<div className="mx-auto max-w-[1440px] p-6 lg:p-8">
									<div className="flex items-center gap-5">
										{ngo.logo && (
											<div className="grid size-20 shrink-0 place-items-center rounded-lg bg-white p-3 shadow-[0_10.8px_28.8px_0_rgba(46,20,0,0.12)]">
												<Image
													src={ngo.logo.location}
													alt={ngo.name}
													height={56}
													aspectRatio={1.6}
													objectFit="contain"
													className="max-h-14 w-full object-contain!"
												/>
											</div>
										)}
										<DialogPrimitive.Title className="text-2xl leading-[1.3] font-medium md:text-[2rem]">
											{t`See the most recent campaigns`}
										</DialogPrimitive.Title>
									</div>

									<div className="mt-8">
										{/* A real skeleton, not an empty box of the right height: the sheet opens instantly
										    (the roster is already loaded) while the campaigns are a fresh fetch behind an
										    hour-long cache, so a blank panel is what the visitor sees for the whole of a
										    cold request and it reads as a partner with nothing to show. */}
										<Suspense fallback={<NgoCampaignsGridSkeleton />}>
											<CampaignsGrid ngo={ngo} page={page} onPageCount={setPageCount} />
										</Suspense>
									</div>
								</div>
							</div>
						</div>
					)}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
